import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryClient from '../../react-query-client';
import store from '../../store';
import { injectIntl } from 'react-intl';
import { NoPrint } from 'react-easy-print';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';

// Redux
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import * as applicationActions from './actions';
import * as prospectActions from '../PeopleProfile/actions';
import { editLease, getSignatureStatuses } from '../LeaseDataTab/actions';
import {
  getAllActivityTypes,
  getAllAffordableRelationships,
  getAllApplicantTypes,
  getAllAssignees,
  getAllContactTypes,
  getAllCountries,
  getAllFloorPlans,
  getAllIncomeTypes,
  getAllNameSuffixes,
  getAllPetBreeds,
  getAllPetTypes,
  getAllPropertyClasses,
  getAllProspectStatus,
  getAllReferralTypes,
  getAllRelationships,
  getAllSpecialNeedsDesignations,
  getAllStates,
  promptToaster,
  selectProperty,
  updateSelectedProperty,
} from '../App/actions';
import * as manageDocumentActions from '../ManageDocuments/actions';
import {
  getAffordableRelationshipOptions,
  getAgedUpMinorApplicantTypeId,
  getApplicantTypeOptions,
  getCountryOptions,
  getGuarantorOptions,
  getIncomeTypeOptions,
  getLiveInCaretakerOptions,
  getMinorRelationshipOptions,
  getNameSuffixOptions,
  getProspectAssigneesOptions,
  getRelationshipOptions,
  getSelectedPropertyClassType,
  getSpecialNeedsDesignationOptions,
  getStateOptions,
} from '../App/selectors';
import {
  getApplication,
  getApplicationHousehold,
  getApplicationStatusOptions,
  getCurrentUser,
  getCustomerId,
  getFinanciallyResponsibleApplicants,
  getFinanciallyResponsibleFullNames,
  getLateMethods,
  getNonfinanciallyResponsibleAdults,
  getScreeningLetterTypes,
  getSecurityDeposits,
} from './selectors';

// Components
import DocumentTitle from 'react-document-title';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { LazyLoad } from '@fortress-technology-solutions/fortress-component-library/HOC';
import TextingTab from '../TextingTab';
import ProfileDetails from './ProfileDetails';
import Snapshot from './Snapshot';
import Household from '../Household';
import LeaseSigning from '../LeaseSigning';
import ManageDocuments from '../ManageDocuments';
import LeaseApplicationTab from './LeaseApplicationTab';
import Ledger from '../Ledger';
import confirm from '../../components/ConfirmDialogModal';
import alert from '../../components/AlertDialogModal';
import ProspectInfoTab from '../PeopleProfile/ProspectInfoTab';
import ProspectInfoTabEditMode from '../PeopleProfile/ProspectInfoTabEditMode';
import LeaseDataTab from '../LeaseDataTab';
import LeaseDataTabEmpty from '../LeaseDataTabEmpty';
import HousingAssistanceVoucherTab from '../HousingAssistanceVoucher';
import {
  recordActivity,
  scheduleActivity,
} from '../../components/ActivityModal';
import { createActivity } from '../../components/CreateActivityModal';
import { editCompletedActivity } from '../../components/EditCompletedActivityModal';
import { editPendingActivity } from '../../components/EditPendingActivityModal';
import PersonalInformationModal, {
  PERSONAL_INFORMATION_FORM,
} from '../../components/PersonalInformationModal';
import { MiniProfileBridge } from '../ResidentProfile/MiniProfileBridge';
import AddHouseholdMemberModal from '../ResidentProfile/AddHouseholdMemberModal';
import { isEditHouseholdMemberDatesFlagEnabled } from '../ResidentProfile/hooks';
import SelectFloorPlanModal, {
  SELECT_FLOOR_PLAN_MODAL,
} from './SelectFloorPlanModal';
import CamTab from '../CamTab';
import CommercialLeaseDataTab from '../CommercialLeaseDataTab';
import Tenant from '../Tenant';
import ActivityContainer from '../../components/ActivityContents';
import Tabs from './Tabs';
import HouseholdNotifications from 'components/HouseholdNotifications';

// Types
import type {
  ActivityType,
  ContactType,
  DropdownOption,
  GlobalState,
  PaginationMeta,
  PetBreed,
  PetType,
  ReferralType,
  SelectOption,
} from '../App/types';
import type { Applicant, Application } from '../CreateApplication/types';
import type { DecisionStatus, HouseholdMember } from './types';
import type { Activity, FormProspect } from '../ProspectProfile/types';
import type { FloorPlan } from '../ManageFloorPlans/types';

// Services
import ActivityTypeService from '../../services/activityTypeService';
import AffordableRelationshipService from '../../services/affordableRelationshipService';

// Constants
import messages from './messages';
import {
  RD_PROGRAM_NAME,
  PH_PROGRAM_NAME,
} from '../GeneralAffordable/constants';

// Utils
import {
  and,
  curryN,
  equals,
  filter,
  head,
  intersection,
  isEmpty,
  isNil,
  not,
  path,
  pathEq,
  pathOr,
  pick,
  propEq,
} from 'ramda';
import moment from 'moment';
import { parse } from 'qs';
import {
  ELECTRONIC_SIGNING_METHOD,
  getSentAndExecutedStatus,
  MANUAL_SIGNING_METHOD,
  mapCommercialLeaseBasicsToLeaseData,
  mapFormValuesLeaseData,
} from '../../utils/lease-helpers';
import {
  configureResidentApplicantAffordableTabsDisplay,
  resolveIsAffordableContext,
} from '../AffordableQualificationTab/Shared/utils';
import {
  getIsReceivingAssistance,
  getNonOptionalChargeFromQualifications,
  chkAllTabsApproved,
  getUnapprovedProgramNames,
  chkAllMoveInCertsApproved,
  getUnapprovedMoveInProgramNames,
  chkAllTabsForComplianceApprovalStatuses,
  COMPLIANCE_APPROVAL_STATUSES,
} from '../../utils/affordable';
import {
  getProspectToSubmit,
  nBedsArrInitialValueBuilder,
} from '../../utils/prospectPreferences-helpers';
import {
  navigateToUrlWithSelectedPropertyId,
  getUrlWithSelectedPropertyId,
} from '../../utils/navigation-helpers';
import {
  getScheduledMoveInDate,
  getProspectTabMoveInDates,
} from '../ManageProspects/utils';
import ProfileLayout from '../../components/ProfileLayout';
import Waitlist from './Waitlist';
import NaurWarning from '../../components/NaurWarningWrapper';
import { TAB_KEYS } from '../ResidentProfile/constants';
import { checkIsSection236 } from '../ResidentProfile/utils';
import PropertyFeesService from '../../services/propertyFeesService';
import {
  setProratedRentAmount,
  setProratedOtherAmount,
} from '../../utils/prorateCalculator';
import { isGeneralAffordableNonHUD } from '../GeneralAffordable/utils';
import { EMPTY_RESIDENT_AFFORDABLE_QUALIFICATIONS } from '../GeneralAffordable/stableEmptyDefaults';
import { invalidateAffordableQualificationQueries } from '../GeneralAffordable/hooks';

import { LeaseDataTabProvider } from '../LeaseDataTab/context';
import { AUTOMATED_TRANSACTION_TYPES } from './constants';
import { filterTrackedAutoFilledFields } from '../../constants/autoFilledLeaseChargeFields';

const GeneralAffordableNonHUD = LazyLoad(() =>
  import('../GeneralAffordable/GeneralAffordable.NonHUD'),
);
const GeneralAffordableHUD = LazyLoad(() =>
  import('../GeneralAffordable/GeneralAffordable.HUD'),
);
const PublicHousing = LazyLoad(() =>
  import('../GeneralAffordable/PH/PublicHousing'),
);
const GeneralAffordableRD = LazyLoad(() =>
  import('../GeneralAffordable/GeneralAffordable.RD'),
);

type AutomatedTransactionType = {
  id: string,
  type: string,
  description: string,
};

type AutoFilledLeaseChargeFields = {
  [key: string]: boolean,
};
type ModifiedAutoFilledLeaseChargeFields = {
  [key: string]: number,
};

type Props = {
  customerId: string,
  activeTab: string,
  certTab: string | number | null,
  currentUser: any,
  users: Array<DropdownOption>,
  applicationHousehold: Array<HouseholdMember>,
  activityTypes: Array<ActivityType>,
  locale: string,
  petTypes: Array<PetType>,
  petBreeds: Array<PetBreed>,
  contactTypes: Array<ContactType>,
  referralTypes: Array<ReferralType>,
  pendingActivities: Array<Activity>,
  completedActivities: Array<Activity>,
  completedActivitiesMeta: PaginationMeta,
  allActivities: Array<Activity>,
  allActivitiesMeta: PaginationMeta,
  columnOrder: Object,
  screening: Object,
  frNames: string[],
  nonfrNames: string[],
  assignedUnits: Array<Object>,
  financiallyResponsibleHousehold: Array<HouseholdMember>,
  propertyClassType: string,
  openFiscalPeriod: Object,
  deletedApplicants: Array<Object>,
  selectedProperty: ?Object,
  leaseSignatureStatuses: Object,
  propertyClasses: Array<Object>,
  affordableQualifications: Array<Object>,
  fpNonOptionalCharges: Array<Object>,
  floorPlans: Array<FloorPlan>,
  relationships: Array<Object>,
};
type InjectedProps = {
  intl: any,
  actions: Object,
  currentApplicationId: string,
  currentApplication: Application,
  leaseRentPercentage: string,
  leaseTerms: Array<any>,
  lateMethods: Array<any>,
  screeningLetterTypes: Array<any>,
  securityDeposits: Array<any>,
  applicationDecisionStatus: Array<DecisionStatus>,
  applicationStatuses: Array<DropdownOption>,
  history: {
    push: Function,
  },
  monthlyTransactions: Array<Object>,
  selectedMonthlyOption: string,
  openFiscalPeriod: Object,
  states: Array<SelectOption>,
};
type State = {
  activityHistoryField: string,
  activityHistoryOrder: string,
  activityHistoryCurrentPage: number,
  activityHistoryLimit: number,
  editMode: boolean,
  editModeProspectInfo: boolean,
  editModeRequiresNote: boolean,
  retrievedPendingActivities: boolean,
  modal: ?string,
  personToEdit: Object,
  key: string,
  activityTableUpdateTrigger: boolean,
  modifiedAutoFilledLeaseChargeFields: ModifiedAutoFilledLeaseChargeFields,
  automatedTransactionTypes: Array<AutomatedTransactionType>,
  autoFilledLeaseChargeFields: AutoFilledLeaseChargeFields,
  leaseWithAutoFilledCharges: Object,
};

export class ApplicationProfile extends Component<
  Props & InjectedProps,
  State,
> {
  constructor(props: Props & InjectedProps) {
    super(props);
    this.state = {
      activityHistoryField: 'dateTime',
      activityHistoryOrder: 'DESC',
      activityHistoryPageCount: 0,
      activityHistoryCurrentPage: 1,
      activityHistoryCurrentCount: 0,
      activityHistoryTotalCount: 0,
      activityHistoryLimit: 10,
      editMode: false,
      editModeProspectInfo: false,
      editModeRequiresNote: false,
      hasHouseholdRentableItems: false,
      retrievedPendingActivities: false,
      refetchHouseholdRentableItemsTrigger: 0,
      modal: null,
      personToEdit: {},
      // Mini-Profile Add/Edit (this card) -- same two pieces of state
      // ResidentProfile's own addOccupant/handleAddHouseholdMemberTypeContinue/
      // editOccupant use, so the Household organism's Add chooser -> Mini-
      // Profile flow behaves identically here.
      occupantToEdit: null,
      newMemberType: '',
      key: this.props.activeTab,
      activityTableUpdateTrigger: false, // tells activity table to re-query activities
      applicationUpdating: false,
      propertyFees: [],
      modifiedAutoFilledLeaseChargeFields: {},
      automatedTransactionTypes: [],
      autoFilledLeaseChargeFields: {},
      leaseWithAutoFilledCharges: {},
    };
    this.props.actions.getAllPropertyClasses();
  }

  componentDidMount() {
    this.props.actions.getAllActivityTypes();
    this.props.actions.getAllAssignees();
    this.props.actions.getAllApplicationDecisionStatus();
    this.props.actions.getAllApplicationStatus();
    this.props.actions.getAllPetTypes();
    this.props.actions.getAllPetBreeds();
    this.props.actions.getAllContactTypes();
    this.props.actions.getAllFloorPlans();
    this.props.actions.getAllReferralTypes();
    this.props.actions.updateSelectedProperty();
    this.props.actions.getOneApplication(this.props.currentApplicationId);
    this.props.actions.updateColumnsSortValue('dateTime', 'descending');
    this.props.actions.getScreeningStatus(this.props.currentApplicationId);
    this.props.actions.getAssignedUnits(this.props.currentApplicationId);
    this.props.actions.getMonthlyTransactions(this.props.currentApplicationId);
    this.props.actions.getAllStates();
    this.props.actions.getAllCountries();
    this.props.actions.getAllIncomeTypes();
    this.props.actions.getAllSpecialNeedsDesignations();
    // App-level master data the Mini-Profile's Type/Relation/Affordable
    // Relation dropdowns are built from -- ResidentProfile already loads
    // these on mount (index.js:452-455); this page never did, which is why
    // those selects came up empty when opened from Manage Applications
    // (ENG-30765).
    this.props.actions.getAllNameSuffixes();
    this.props.actions.getAllAffordableRelationships();
    this.props.actions.getAllApplicantTypes();
    this.props.actions.getAllRelationships();
    this.props.actions.getDocumentTypes(this.props.currentApplicationId);
    this.props.actions.getResidentLetterDocumentTypes(
      this.props.currentApplicationId,
    );
    this.getAffordableRelationships();
    if (this.props.selectedProperty) {
      this.getPropertyFees({
        organizationId: this.props.selectedProperty.organizationId,
        propertyId: this.props.selectedProperty.id,
      });
    }
    this.getAutomatedTransactionTypes();
  }

  componentWillUnmount() {
    this.props.actions.cleanLoadedApplication();
  }

  componentDidUpdate(prevProps: Object, prevState: Object) {
    const { actions, currentApplication, currentApplicationId } = this.props;
    const { currentApplication: prevCurrentApplicationProp } = prevProps;

    if (
      this.state.modal === PERSONAL_INFORMATION_FORM &&
      this.state.personToEdit?.id &&
      prevProps.applicationHousehold !== this.props.applicationHousehold
    ) {
      const currentApplicant = this.props.applicationHousehold.find(
        (applicant) => applicant.id === this.state.personToEdit.id,
      );
      const prevApplicant = prevProps.applicationHousehold.find(
        (applicant) => applicant.id === this.state.personToEdit.id,
      );

      if (currentApplicant && !equals(currentApplicant, prevApplicant)) {
        // $FlowFixMe
        const unit = pathOr(null, ['au', 'unit'], currentApplication);
        // $FlowFixMe
        const personToEdit = {
          ...currentApplicant,
          ...(!!unit && { unitNumber: unit.unitNumber }),
          customerStatusTitle: 'Applicant',
          isCommercial: currentApplication.isCommercial,
        };
        this.setState({ personToEdit });
      }
    }

    // To trigger a refresh in the ActivityTable
    if (
      typeof prevProps.currentApplication?.assignedTo?.id === 'string' &&
      typeof currentApplication?.assignedTo?.id === 'string' &&
      prevProps.currentApplication?.assignedTo?.id !==
        currentApplication?.assignedTo?.id
    ) {
      this.refreshActivityTable();
    }

    if (
      this.state.retrievedPendingActivities === false &&
      currentApplication.prospectInfo.id
    ) {
      this.setState({ ...this.state, retrievedPendingActivities: true });
      actions.getProspectPendingActivities(currentApplication.prospectInfo.id);
      actions.getScreeningStatus(currentApplicationId);
      this.refreshActivities();
    }
    const prevLeaseId = pathOr(
      null,
      ['currentApplication', 'lease', 'id'],
      prevProps,
    );
    const prevDesiredSignatureMethod = pathOr(
      null,
      ['currentApplication', 'lease', 'desiredSignatureMethod'],
      prevProps,
    );
    // $FlowFixMe
    const leaseId = pathOr(null, ['lease', 'id'], currentApplication);
    // $FlowFixMe
    const desiredSignatureMethod = pathOr(
      null,
      ['lease', 'desiredSignatureMethod'],
      currentApplication,
    );
    if (
      (prevLeaseId !== leaseId ||
        prevDesiredSignatureMethod !== desiredSignatureMethod) &&
      desiredSignatureMethod === ELECTRONIC_SIGNING_METHOD
    ) {
      actions.getSignatureStatuses(leaseId);
    }

    const leaseWithAutoFilledChargesStateChanged = !_.isEqual(
      this.state.leaseWithAutoFilledCharges,
      prevState.leaseWithAutoFilledCharges,
    );
    const propertyFeesChanged = !_.isEqual(
      this.state.propertyFees,
      prevState.propertyFees,
    );
    const automatedTransactionTypesChanged = !_.isEqual(
      this.state.automatedTransactionTypes,
      prevState.automatedTransactionTypes,
    );
    const lease = currentApplication.lease;
    const leaseIsLocked = lease?.isLocked;
    const leaseWithAutoFilledChargesIsEmpty = _.isEmpty(
      this.state.leaseWithAutoFilledCharges,
    );
    const leaseChanged = !_.isEqual(lease, prevCurrentApplicationProp.lease);

    if (
      this.props.flags.automatedLeaseChargesP1 &&
      !leaseIsLocked &&
      (leaseWithAutoFilledChargesStateChanged ||
        leaseWithAutoFilledChargesIsEmpty ||
        propertyFeesChanged ||
        automatedTransactionTypesChanged ||
        leaseChanged)
    ) {
      this.setState((prevState) => ({
        ...prevState,
        leaseWithAutoFilledCharges: {
          ...prevState.leaseWithAutoFilledCharges,
          ...lease,
          ...this.populateAutomatedLeaseCharges(lease, this.state.propertyFees),
        },
      }));
    }

    if (
      propertyFeesChanged ||
      automatedTransactionTypesChanged ||
      leaseChanged
    ) {
      this.setState((prevState) => ({
        ...prevState,
        leaseWithAutoFilledCharges: {
          ...prevState.leaseWithAutoFilledCharges,
          ...lease,
        },
      }));
    }
  }

  static getDerivedStateFromProps(props: any) {
    const queryString = parse(
      pathOr('', ['location', 'search'], props).replace('?', ''),
    );
    const selectedPropertyId = pathOr('', ['selectedProperty', 'id'], props);
    const { propertyId: queryPropertyId } = queryString;
    const userProperties = pathOr(
      [],
      ['currentUser', 'user', 'properties'],
      props,
    );
    if (
      queryPropertyId &&
      queryPropertyId !== selectedPropertyId &&
      userProperties.some((p) => p.id === queryPropertyId)
    ) {
      const newProperty = userProperties.find((p) => p.id === queryPropertyId);
      props.actions.selectProperty(newProperty);
    } else if (!selectedPropertyId) {
      props.history.push('/');
    }
    return null;
  }

  onActivityHistoryOrder = (field: string) => {
    const columnOrder = this.props.columnOrder[field];
    const order = columnOrder === 'ascending' ? 'DESC' : 'ASC';
    const icon =
      columnOrder === 'sortable'
        ? 'ascending'
        : columnOrder === 'ascending'
        ? 'descending'
        : 'ascending';
    this.props.actions.updateColumnsSortValue(field, icon);
    this.setState({ activityHistoryField: field, activityHistoryOrder: order });
    this.props.actions.getProspectAllActivities(
      this.props.currentApplication.prospectInfo.id,
      this.state.activityHistoryCurrentPage,
      this.state.activityHistoryLimit,
      field,
      order,
    );
    this.props.actions.getProspectCompletedActivities(
      this.props.currentApplication.prospectInfo.id,
      this.state.activityHistoryCurrentPage,
      this.state.activityHistoryLimit,
      field,
      order,
    );
  };
  checkUserPermissions = (testScope: Array<string>) => {
    const userScopes = this.props.currentUser.permissions.map(
      (permission) => permission.scope,
    );
    return intersection(testScope, userScopes).length > 0;
  };

  refreshActivities() {
    if (!!this.props.currentApplication) {
      this.props.actions.getProspectAllActivities(
        this.props.currentApplication.prospectInfo.id,
        this.state.activityHistoryCurrentPage,
        this.state.activityHistoryLimit,
        this.state.activityHistoryField,
        this.state.activityHistoryOrder,
      );
      this.props.actions.getProspectCompletedActivities(
        this.props.currentApplication.prospectInfo.id,
        this.state.activityHistoryCurrentPage,
        this.state.activityHistoryLimit,
        this.state.activityHistoryField,
        this.state.activityHistoryOrder,
      );
    }
  }

  onActivityHistoryPageChange(pageNumber: number) {
    this.setState({ activityHistoryCurrentPage: pageNumber }, () =>
      this.refreshActivities(),
    );
  }

  getReduxStore = () => this.context?.store || store;

  onRecordActivityClick = async () => {
    const activityTypeService = new ActivityTypeService();
    const activityTypesList = activityTypeService.generateActivityTypesList(
      this.props,
    );
    const users = this.props.users;
    /* istanbul ignore else */
    if (this.props?.currentApplication?.prospectInfo?.id != null) {
      const data: Activity = await recordActivity(
        this.getReduxStore(),
        this.props.intl,
        this.props.currentApplication.prospectInfo,
        activityTypesList,
        users,
        'applicant',
        this.props.currentApplicationId,
      );
      this.props.actions.createProspectActivity(
        Object.assign(data, { customerStatus: 'Applicant' }),
      );
      this.setState({ activityHistoryCurrentPage: 1 });
    }
  };

  getAffordableRelationships = async () => {
    const affordableRelationshipService = new AffordableRelationshipService();
    const affordableRelationships =
      await affordableRelationshipService.getAll();
    this.setState({ affordableRelationships });
  };

  getPropertyFees = async ({ organizationId, propertyId }) => {
    try {
      const propertyFeesService = new PropertyFeesService();
      const propertyFees = await propertyFeesService.getAllPropertyFees(
        organizationId,
        propertyId,
      );
      this.setState({ propertyFees });
    } catch (error) {
      this.props.actions.promptToaster({
        type: 'error',
        message: this.props.intl.formatMessage(messages.propertyFeesFetchError),
      });
    }
  };

  getAutomatedTransactionTypes = async () => {
    try {
      const propertyFeesService = new PropertyFeesService();
      const automatedTransactionTypes =
        await propertyFeesService.getAutomatedTransactionTypes();
      this.setState({ automatedTransactionTypes });
    } catch (error) {
      this.props.actions.promptToaster({
        type: 'error',
        message: this.props.intl.formatMessage(
          messages.automatedTransactionTypesFetchError,
        ),
      });
    }
  };

  onScheduleActivityClick = async () => {
    const activityTypeService = new ActivityTypeService();
    const activityTypesList = activityTypeService.generateActivityTypesList(
      this.props,
    );
    const users = this.props.users;
    /* istanbul ignore else */
    if (this.props?.currentApplication?.prospectInfo?.id != null) {
      const data: Activity = await scheduleActivity(
        this.getReduxStore(),
        this.props.intl,
        this.props.currentApplication.prospectInfo,
        activityTypesList,
        users,
        'applicant',
        this.props.currentApplicationId,
      );
      this.props.actions.createProspectActivity({
        ...data,
        customerStatus: 'Applicant',
      });
      this.setState({ activityHistoryCurrentPage: 1 });
    }
  };

  onCreateActivityClick = async (refresh?: Function) => {
    const activityTypeService = new ActivityTypeService();
    const activityTypesList = activityTypeService.generateActivityTypesList(
      this.props,
    );
    const users = this.props.users;
    if (this.props?.currentApplication?.prospectInfo?.id != null) {
      const data: Activity = await createActivity({
        store: this.getReduxStore(),
        intl: this.props.intl,
        prospect: this.props.currentApplication.prospectInfo,
        activityTypesList: activityTypesList,
        assigneeList: users,
        stage: 'applicant',
        urlId: this.props.currentApplicationId,
      });
      this.props.actions.createProspectActivity(
        {
          ...data,
          customerStatus: 'Applicant',
        },
        refresh,
      );
      this.setState({ activityHistoryCurrentPage: 1 });
    }
  };
  onEditActivity = (activity: Activity, refresh?: Function) => {
    const activityTypeService = new ActivityTypeService();
    const activityTypesList = activityTypeService.generateActivityTypesList(
      this.props,
    );
    const users = this.props.users;
    const stageInfo = {
      currentStage: 'applicant',
      urlId: this.props.currentApplicationId,
    };

    const editPromise = activity.activityCompletionStatusId
      ? editCompletedActivity(
          this.getReduxStore(),
          this.props.intl,
          activity,
          stageInfo,
        )
      : editPendingActivity(
          this.getReduxStore(),
          this.props.intl,
          activity,
          activityTypesList,
          users,
          this.props.currentApplication.prospectInfo,
          stageInfo,
        );

    return editPromise.then((data: Activity) => {
      if (data.saveAndClose) {
        this.props.actions.updateProspectActivity(data, refresh);
      } else if (data.saveAndAddNew) {
        this.props.actions.updateProspectActivity(data, refresh);
        this.onCreateActivityClick(this.forceRefreshActivityTable);
      } else {
        this.props.actions.deleteProspectActivity(data, refresh);
      }
      this.setState({ activityHistoryCurrentPage: 1 });
    });
  };
  handleAssignedToChange = (event: Object, newUserId: string) => {
    this.props.actions.assignApplication({
      ...this.props.currentApplication,
      assignedToId: newUserId,
    });
  };

  handleConvertToResident = () => {
    const route = `/create-resident/${this.props.currentApplicationId}`;
    this.convertApplication(route);
  };

  handleConvertToCommercialTenant = () => {
    const route = `/create-commercial-tenant/${this.props.currentApplicationId}`;
    this.convertApplication(route);
  };

  convertApplication = async (route: string) => {
    navigateToUrlWithSelectedPropertyId(route);
  };

  updateApplication = (changes: any) => {
    this.setState({ applicationUpdating: true });
    const { currentApplication } = this.props;
    this.props.actions.updateApplication(
      {
        // $FlowFixMe
        ...pick(
          [
            'id',
            'unitTypeId',
            'prospectId',
            'propertyId',
            'notes',
            'vehicleCount',
            'applicationStatusId',
            'updatedById',
            'propertyClassId',
            'adults',
            'minors',
            'pets',
            'fees',
            'affordableQualifyingChecklist',
            'publicHousingAuthority',
            'hudIncomeLimitId',
            'affordableDocuments',
            'reCompleteCertificationStarted',
            'householdUtilityAllowanceId',
            'voucherEffectiveDate',
            'rentalAssistanceSourceId',
            'complianceApprovalId',
            'setAsideProgramId',
          ],
          currentApplication,
        ),
        ...changes,
      },
      changes.notes
        ? 'successDescriptionNotes'
        : 'successDescriptionApplication',
      null,
      () => {
        this.refreshActivityTable();
        this.refreshHouseholdRentableItems();
        this.setState({ applicationUpdating: false });
      },
    );
    this.props.actions.getOneApplication(this.props.currentApplicationId);
  };

  updatePropertyClass = (changes: any) => {
    const { currentApplication } = this.props;
    this.props.actions.updatePropertyClass(
      {
        // $FlowFixMe
        ...pick(['id', 'propertyClassId'], currentApplication),
        ...changes,
      },
      undefined,
      this.refreshActivityTable,
    );
  };

  handleApplicationStatusChange = (event: Object, newStatusId: string) => {
    this.updateApplication({ applicationStatusId: newStatusId });
  };

  handleApplicationNoteUpdate = (changes: Object) => {
    this.updateApplication({ notes: changes });
  };

  handleApplicationPropertyClassUpdate = (changes: Object) => {
    if (!!this.props.currentApplication.au) {
      alert('To change unit type, please remove assigned unit first.');
    } else {
      confirm(this.props.intl.formatMessage(messages.convertClass), {
        intl: this.props.intl,
      }).then(() => {
        this.updatePropertyClass({ propertyClassId: changes.propertyClassId });
      });
    }
  };

  handleCommercialContactsCheckboxClick = (e: any) => {
    this.updateApplication({ anyContact: !e.target.checked });
  };

  handleCommercialInsuranceCheckboxClick = (e: any) => {
    this.updateApplication({ anyInsurance: !e.target.checked });
  };

  handleNoVehiclesChange = (vehicleCount: number | null) => () => {
    vehicleCount === null
      ? this.updateApplication({ vehicleCount: 0 })
      : this.updateApplication({ vehicleCount: null });
  };

  handleChecklistOptionChange = (
    applicant: Object,
    item: Object,
    type: string,
    { target: { value } }: Object,
  ) => {
    const checklistChanges = {
      applicationId: this.props.currentApplicationId,
      applicantChecklistId: applicant.applicantChecklistId,
      checklistItemId: item.checklistItemId,
      checklistItemOptionId: type === 'option' ? value : item.selectedOption,
      applicantChecklistDecisionId: type === 'decision' ? value : null,
    };
    return this.props.actions.updateApplicantChecklist(checklistChanges);
  };

  handleMultipleChecklistOptionsChange = (applicant, items, callback) => {
    const checklistChanges = items.map(
      ({ checklistItemId, applicantChecklistDecisionId }) => ({
        checklistItemId,
        applicantChecklistDecisionId: applicantChecklistDecisionId ?? null,
      }),
    );

    return this.props.actions.updateMultipleApplicantChecklistFields(
      checklistChanges,
      this.props.currentApplicationId,
      applicant.applicantChecklistId,
      callback,
    );
  };
  handleProspectInfoEdit = () => {
    this.setState({ editModeProspectInfo: !this.state.editModeProspectInfo });
  };

  getDropDownOptions = (list: Array<ContactType | ReferralType>) => {
    const options: Array<Object> = list
      .map((item) => ({
        value: item.id,
        text: item.translations[this.props.locale] || item.name,
      }))
      .sort((a: Object, b: Object) => a.text.localeCompare(b.text));
    options.unshift({
      value: '',
      text: this.props.intl.formatMessage(messages.chooseOption),
      disabled: true,
    });
    return options;
  };

  getDropDownOptionsForPets = (list: Array<PetType>): Array<Object> =>
    list.map((item) => ({
      value: item.id,
      text: item.translations[this.props.locale] || item.name,
      breeds: item.petBreeds.map((itemBreed) => ({
        value: itemBreed.id,
        text: itemBreed.translations[this.props.locale] || itemBreed.name,
      })),
    }));

  getProspectForForm = (): FormProspect | {} => {
    if (!this.props.currentApplication.prospectInfo) return {};
    const prospect = this.props.currentApplication.prospectInfo;
    // $FlowFixMe
    const objP = pick(
      [
        'firstName',
        'lastName',
        'emailAddress',
        'phoneNumber',
        'contactTypeId',
        'referralTypeId',
        'assignedTo',
        'prospectStatusId',
      ],
      this.props.currentApplication.prospectInfo,
    );
    const spouse = filter(
      propEq('type', 'Spouse'),
      prospect.additionalOccupants,
    );
    const kids = filter(propEq('type', 'Minor'), prospect.additionalOccupants);
    const otherAdults = filter(
      propEq('type', 'Other Adult'),
      prospect.additionalOccupants,
    );
    const { moveInDateFrom, moveInDateTo } = getProspectTabMoveInDates({
      prospectPreferences: prospect.prospectPreferences,
      isUnitAssigned: Boolean(this.props?.currentApplication?.au?.unitId),
      isWaitlistV2Enabled: this.props.flags.affordableWaitlistV2,
    });
    return {
      ...objP,
      spouse: spouse,
      otherAdults: otherAdults,
      kids: kids,
      pets: prospect.petOccupants,
      spouseChecked: spouse.length > 0,
      otherAdultsChecked: otherAdults.length > 0,
      otherAdultsAmount: otherAdults.length,
      kidsChecked: kids.length > 0,
      kidsAmount: kids.length,
      petsChecked: prospect.petOccupants.length > 0,
      petsAmount: prospect.petOccupants.length,
      nBaths: prospect.prospectPreferences.nBaths,
      nBedsArr: nBedsArrInitialValueBuilder(
        prospect.prospectPreferences.nBedsArr,
        prospect.prospectPreferences.nBeds,
      ),
      nHalfBaths: prospect.prospectPreferences?.nHalfBaths,
      nBeds: prospect.prospectPreferences.nBeds,
      moveInDateFrom: this.props.flags.assigningUnitsMoveInDates
        ? moveInDateFrom
        : prospect.prospectPreferences.moveInDateFrom,
      moveInDateTo: this.props.flags.assigningUnitsMoveInDates
        ? moveInDateTo
        : prospect.prospectPreferences.moveInDateTo,
      moveInDateScheduled: prospect.prospectPreferences.moveInDateScheduled,
      unitId: prospect.prospectPreferences.unitId,
      isUnitAssigned: Boolean(this.props.currentApplication.au),
      priceFrom: prospect.prospectPreferences.priceFrom,
      priceTo: prospect.prospectPreferences.priceTo,
      leaseTermInMonths: prospect.prospectPreferences.leaseTermInMonths,
      helpText: prospect.prospectPreferences.helpText,
      wants: prospect.prospectPreferences.wants,
      notWants: prospect.prospectPreferences.notWants,
      noMoveInDate: Boolean(!moveInDateFrom),
      preferredFloorPlanId: prospect.prospectPreferences.preferredFloorPlanId,
      prefersUnitAccessible: prospect.prospectPreferences.prefersUnitAccessible
        ? 'yes'
        : 'no',
    };
  };

  generateRelationships(locale: string) {
    const relationships = this.props.relationships
      ? this.props.relationships.map((suffix) => ({
          value: suffix.id,
          text: suffix.translations[locale] || suffix.name,
        }))
      : [];
    relationships.unshift({
      value: '',
      text: this.props.intl.formatMessage(messages.chooseOption),
      disabled: true,
    });
    return relationships;
  }

  handleSubmitProspectInfoTab = (prospect: FormProspect) => {
    const { prospectInfo } = this.props.currentApplication;
    const relationships = this.generateRelationships(this.props.intl.locale);
    const prospectToSubmit = getProspectToSubmit(
      {
        prospect,
        propertyId: prospectInfo.propertyId,
        currentProspect: prospectInfo,
        relationships,
      },
      this.props.flags,
    );
    this.props.actions.updateProspect(prospectToSubmit, null);
    this.setState({
      editModeProspectInfo: false,
      activityHistoryCurrentPage: 1,
    });
  };

  handleCancelProspectInfoTab = (pristine: boolean) => {
    if (pristine) {
      this.setState({ editModeProspectInfo: false });
    } else {
      confirm(this.props.intl.formatMessage(messages.cancelConfirmation), {
        intl: this.props.intl,
      }).then(
        () => {
          this.setState({ editModeProspectInfo: false });
        },
        () => {
          this.setState({ editModeProspectInfo: true });
        },
      );
    }
  };

  handleEditHousehold = () => {
    const url = `/application/${this.props.currentApplicationId}/household`;
    navigateToUrlWithSelectedPropertyId(url);
  };

  refetchHudProfileForApplication = () => {
    this.props.actions.getOneApplication(this.props.currentApplicationId);
  };

  handleEditApplicationForm = (applicant: Applicant) => {
    const { currentApplicationId } = this.props;
    const isProspect = () =>
      and(
        not(isNil(applicant)),
        pathEq(['applicantCustomer', 'isProspect'], true)(applicant),
      );

    const isFinanciallyResponsible = () =>
      and(
        and(
          not(isNil(applicant)),
          not(isNil(path(['applicantCustomer'], applicant))),
        ),
        and(
          not(isProspect()),
          pathEq(['applicantType', 'financiallyResponsible'], true)(applicant),
        ),
      );

    const isNotFinanciallyResponsible = () =>
      and(
        and(
          not(isNil(applicant)),
          not(isNil(path(['applicantCustomer'], applicant))),
        ),
        and(not(isProspect()), not(isFinanciallyResponsible())),
      );

    if (isProspect()) {
      window.location.assign(
        getUrlWithSelectedPropertyId(
          `/primary-form/${currentApplicationId}/${applicant.id}`,
        ),
      );
    }
    if (isFinanciallyResponsible()) {
      window.location.assign(
        getUrlWithSelectedPropertyId(
          `/non-primary-form/${currentApplicationId}/${applicant.id}`,
        ),
      );
    }
    if (isNotFinanciallyResponsible()) {
      window.location.assign(
        getUrlWithSelectedPropertyId(
          `/shortApplication/${currentApplicationId}/${applicant.id}`,
        ),
      );
    }
  };

  handleCommercialLeaseBasicsSubmit = (
    leaseId?: string,
    values: Object,
    onSettled?: Function,
  ) => {
    const {
      actions: { saveLeaseDataTab },
      currentApplicationId,
      currentApplication,
    } = this.props;
    const unitId = path(['au', 'unit', 'id'], currentApplication);
    const leaseData = {
      ...mapCommercialLeaseBasicsToLeaseData(values),
      unitId,
      desiredSignatureMethod: MANUAL_SIGNING_METHOD,
    };

    saveLeaseDataTab(
      leaseData,
      currentApplicationId,
      undefined,
      undefined,
      undefined,
      onSettled,
    );
  };

  handleAutoFilledFieldChange = (fieldName: string, value: any) => {
    this.setState((prevState) => {
      const { modifiedAutoFilledLeaseChargeFields } = prevState;
      return {
        modifiedAutoFilledLeaseChargeFields: {
          ...modifiedAutoFilledLeaseChargeFields,
          [fieldName]: value,
        },
        autoFilledLeaseChargeFields: {
          ...prevState.autoFilledLeaseChargeFields,
          [fieldName]: false,
        },
      };
    });
  };

  resetProratedRentAutoFilledChangeStatus = () => {
    this.setState((prevState) => {
      const { modifiedAutoFilledLeaseChargeFields } = prevState;
      const updatedFields = { ...modifiedAutoFilledLeaseChargeFields };
      delete updatedFields.proratedRent;
      return {
        modifiedAutoFilledLeaseChargeFields: updatedFields,
        autoFilledLeaseChargeFields: {
          ...prevState.autoFilledLeaseChargeFields,
          proratedRent: true,
          proratedOther: true,
        },
      };
    });
  };

  leaseDataHandleSubmit = (
    values: Object,
    _refreshFn?: Function,
    onSettled?: Function,
  ) => {
    const { currentApplicationId, nonfrNames, currentApplication, flags } =
      this.props;
    const desiredSignatureMethod =
      values.desiredSignatureMethod === ELECTRONIC_SIGNING_METHOD &&
      nonfrNames.length > 0
        ? 'default'
        : values.desiredSignatureMethod;
    const formData = mapFormValuesLeaseData({
      ...values,
      desiredSignatureMethod,
    });
    const currentMoveInDate = moment(
      currentApplication?.lease?.moveInDate,
    ).format('YYYY-MM-DD');
    const updatedMoveInDate = moment(formData?.moveInDate).format('YYYY-MM-DD');
    const hasMoveInDateChanged = flags.assigningUnitsMoveInDates
      ? updatedMoveInDate !== currentMoveInDate
      : false;

    this.props.actions.saveLeaseDataTab(
      formData,
      currentApplicationId,
      this.refreshActivityTable,
      hasMoveInDateChanged,
      this.state.modifiedAutoFilledLeaseChargeFields,
      onSettled,
    );

    return values;
  };

  leaseDataHandleGenerateLease = (values: any) => {
    const applicationId = this.props.currentApplicationId;
    const data = mapFormValuesLeaseData(values);
    this.props.actions.generateLeaseDocument({ applicationId, data });
    return values;
  };

  validateRequired = (value: any) =>
    not(isEmpty(value)) && not(isNil(value)) && !equals('default', value);

  onResendPayleaseClick = () => {
    const {
      customerId,
      actions: { resendPayLeaseEmail },
    } = this.props;
    resendPayLeaseEmail(customerId);
  };

  resetModal = () => {
    // newMemberType is cleared too so a later Add always re-opens
    // unselected, same reasoning as ResidentProfile#resetModal -- harmless
    // for every other modal sharing this same onClose/dismiss handler, since
    // it's only ever read while the Add-Household-Member flow is open.
    this.setState({ modal: null, newMemberType: '' });
  };

  isEditHouseholdMemberDatesEnabled = () => {
    return isEditHouseholdMemberDatesFlagEnabled(this.props.flags);
  };

  // Redux-backed refresh after a Mini-Profile save/create/delete --
  // application.applicants (and everything derived from it, e.g.
  // applicationHousehold) lives in Redux here, not React Query, so
  // MiniProfileBridge's own cache invalidation can't refresh it; this
  // mirrors ResidentProfile#handleMiniProfileRefresh and the same
  // getOneApplication refetch already used elsewhere on this page after a
  // household change (e.g. handleEditHousehold).
  //
  // Also covers Change Head of Household (wired to this same handler via
  // onChangeHeadOfHouseholdSuccess below): both flows can reopen a completed
  // certification server-side, and the LIHTC Qualification tabs/badges read
  // affordableQualifications from a React Query cache (keyed on
  // getHouseholdProfileAffordableQualificationsByProgram) that getOneApplication
  // never touches -- without this the reopened state only showed up after a
  // manual page reload.
  handleMiniProfileSaveSuccess = () => {
    this.refetchApplicationAndQualifications();
  };

  // The desired move-in date also lands on both layers: the Prospect tab reads
  // Redux, the qualification tabs' pre-assignment effective date reads React Query.
  refetchApplicationAndQualifications = () => {
    this.props.actions.getOneApplication(this.props.currentApplicationId);
    invalidateAffordableQualificationQueries(queryClient);
  };

  // Add button (Household/index.js's onAddHouseholdMember) -- opens the
  // member-type chooser first, same two-step flow as
  // ResidentProfile#addOccupant.
  addHouseholdMember = () => {
    if (!this.isEditHouseholdMemberDatesEnabled()) {
      return;
    }
    this.setState({
      occupantToEdit: null,
      newMemberType: '',
      modal: 'addHouseholdMemberType',
    });
  };

  // AddHouseholdMemberModal's chooser -> Mini-Profile create, same handoff
  // as ResidentProfile#handleAddHouseholdMemberTypeContinue.
  handleAddHouseholdMemberTypeContinue = (memberType: string) => {
    this.setState({
      newMemberType: memberType,
      modal: 'editAffordableMiniProfile',
    });
  };

  // Household member name click for both Household (residential) and Tenant
  // (commercial): flag on opens the Mini-Profile, flag off falls back to the
  // legacy PersonalInformationModal. Pets are never reachable here.
  handleHouseholdMemberClick = (member: Object) => {
    if (!this.isEditHouseholdMemberDatesEnabled()) {
      this.editHouseholdMember(member);
      return;
    }
    // application.applicants only reloads on page mount, so an Application Form
    // edit (its own route) would otherwise open stale.
    this.props.actions.getOneApplication(this.props.currentApplicationId);
    this.setState({
      occupantToEdit: member?.id ?? null,
      newMemberType: '',
      modal: 'editAffordableMiniProfile',
    });
  };

  openSelectFloorPlanModal = () => {
    this.setState({ modal: SELECT_FLOOR_PLAN_MODAL });
  };

  editHouseholdMember = (member: Object) => {
    const { currentApplication } = this.props;
    // $FlowFixMe
    const unit = pathOr(null, ['au', 'unit'], currentApplication);
    // $FlowFixMe
    const personToEdit = {
      ...member,
      ...(!!unit && { unitNumber: unit.unitNumber }),
      customerStatusTitle: 'Applicant',
      isCommercial: currentApplication.isCommercial,
    };
    this.setState({
      personToEdit,
      modal: PERSONAL_INFORMATION_FORM,
    });
  };

  sendPortalInviteEmail = (customerId: string) => {
    const { actions, currentApplication, currentApplicationId } = this.props;
    // $FlowFixMe
    const leaseId = pathOr(null, ['lease', 'id'], currentApplication);
    actions.sendApplicantPortalInviteEmail(
      customerId,
      currentApplicationId,
      leaseId,
      currentApplication.isCommercial ? 'commercial-applicant' : 'applicant',
      () => {
        if (
          this.state.modal === PERSONAL_INFORMATION_FORM &&
          this.state.personToEdit?.id === customerId
        ) {
          const personToEdit = {
            ...this.state.personToEdit,
            portalUser: {
              ...(this.state.personToEdit.portalUser || {}),
              claimRequestedAt: new Date().toISOString(),
              claimToken: null, // Token will be set when application data refreshes
              isClaimed: false,
            },
          };
          this.setState({ personToEdit });
        }
      },
    );
  };

  refreshActivityTable = () => {
    this.setState({
      activityTableUpdateTrigger: !this.state.activityTableUpdateTrigger,
    });
  };

  refreshHousingAssistanceVouchersTable = () => {
    queryClient.invalidateQueries(['getAllVouchersByHouseholdId']);
  };

  refreshHouseholdRentableItems = () => {
    this.setState({
      refetchHouseholdRentableItemsTrigger:
        this.state.refetchHouseholdRentableItemsTrigger + 1,
    });
  };

  // At the moment of triggering the above from the modals it doesn't actually refreshes the table properly
  forceRefreshActivityTable = () => {
    this.setState(
      {
        activityTableUpdateTrigger: !this.state.activityTableUpdateTrigger,
      },
      () => this.refreshActivities(),
    );
  };

  unassignUnitHandler = async () => {
    const {
      actions: { unassignUnit, editLease },
      assignedUnits,
      currentApplication,
      currentApplicationId,
    } = this.props;
    const assignedUnit =
      assignedUnits && assignedUnits.length > 0 ? assignedUnits[0] : null;
    if (assignedUnit) {
      const {
        lease,
        lease: { id: leaseId, documentId },
      } = currentApplication;
      const { leaseExecuted, leaseSentToPortal } =
        getSentAndExecutedStatus(lease);
      if (leaseId && (leaseExecuted || leaseSentToPortal)) {
        await editLease(
          leaseId,
          currentApplicationId,
          false,
          currentApplicationId,
          !!documentId,
        );
      } else {
        await unassignUnit(assignedUnit.id, currentApplicationId, () => {
          this.refreshActivityTable();
          this.refreshHouseholdRentableItems();
          this.refreshHousingAssistanceVouchersTable();
        });
      }
    }
  };

  handleEditApplication = ({
    generalInformation,
    reportingInformation,
  }: Object) => {
    if (this.props.currentApplication.isCommercial) {
      const nullEmptyFields = (data: Object) =>
        Object.entries(data).reduce((acc, [key, value]) => {
          acc[key] = value ? value : null;
          return acc;
        }, {});

      const payload = nullEmptyFields({
        tenantLegalName: generalInformation.firstName,
        doingBusinessAs: generalInformation.preferredName,
        parentCompany: generalInformation.parentCompany,
        phoneNumber: generalInformation.phoneNumber,
        emailAddress: generalInformation.emailAddress,
        street: generalInformation.street,
        street2: generalInformation.street2,
        city: generalInformation.city,
        state: generalInformation.state,
        zipCode: generalInformation.zipCode,
      });

      this.props.actions.updateCommercialApplication({
        ...payload,
        id: this.props.currentApplicationId,
      });
    }

    const selectedApplicantId = this.state.personToEdit.id;
    const currentApplicant =
      this.props.currentApplication?.applicants?.find(
        (applicant) => applicant.id === selectedApplicantId,
      ) ?? null;
    if (!currentApplicant) {
      return;
    }
    const applicantType = currentApplicant?.applicantType?.type ?? 'Adult';
    const isAdult = applicantType === 'Adult';

    const specialNeedsDesignationIds =
      reportingInformation?.specialNeedsDesignationIds ?? [];
    const annualIncome = reportingInformation?.annualIncome ?? '0.00';
    const applicantMinorId = currentApplicant?.applicantMinor?.id ?? '';
    const payload = {
      specialNeedsDesignationIds,
      annualIncome,
      isAdult,
      applicantId: selectedApplicantId,
      applicantMinorId,
    };
    this.props.actions.updateApplicant(payload);
    queryClient.invalidateQueries([
      'getHouseholdProfileAffordableQualificationsByProgram',
    ]);
  };

  onZeroBalance = () => {
    const {
      currentApplication,
      actions: { getOneApplication },
      currentApplicationId,
    } = this.props;
    const customerStatus = currentApplication?.householdInfo?.mainCus?.status;
    const applicationStatus = currentApplication?.applicationStatus?.name;
    const areFeesPaid = currentApplication?.areFeesPaid ?? false;
    if (
      !areFeesPaid &&
      (customerStatus === 'Applicant' || applicationStatus === 'In Process')
    ) {
      getOneApplication(currentApplicationId);
    }
  };

  setHasHouseholdRentableItems = (hasHouseholdRentableItems: boolean) => {
    this.setState({ hasHouseholdRentableItems });
  };

  captureAutoFilledFields = (autoFilledFields) => {
    this.setState((prevState) => ({
      ...prevState,
      autoFilledLeaseChargeFields: {
        ...prevState.autoFilledLeaseChargeFields,
        ...filterTrackedAutoFilledFields(autoFilledFields),
      },
    }));
  };

  getFeeAmounts = (fees: Array<Object>, lease: Object) => {
    const commonParams = [fees, lease];
    const amountsByType = {
      monthlyPetRent: this.getAmountByType(...commonParams, 'monthlyPetRent'),
      petDeposit: this.getAmountByType(...commonParams, 'petDeposit'),
      petFee: this.getAmountByType(...commonParams, 'petFee'),
      shortTermFee: this.getAmountByType(...commonParams, 'shortTermFee'),
      securityDeposit: this.getAmountByType(...commonParams, 'securityDeposit'),
      nonRefundableAdminFee: this.getAmountByType(
        ...commonParams,
        'nonRefundableAdminFee',
      ),
    };
    const autoFilledFields = Object.keys(amountsByType).reduce((acc, key) => {
      if (amountsByType[key] > 0) {
        acc[key] = true;
      }
      return acc;
    }, {});

    return { ...amountsByType, autoFilledFields };
  };

  getAmountByType = (fees, lease, type) => {
    const { automatedTransactionTypes } = this.state;
    const modifiedAutoFilledLeaseChargeFieldsFromLease =
      lease?.modifiedAutoFilledLeaseChargeFields || [];
    const fieldWasModified = (fieldName) => {
      const modifiedFieldNames =
        modifiedAutoFilledLeaseChargeFieldsFromLease.map(
          (field) => field.fieldName,
        );
      return modifiedFieldNames.includes(fieldName);
    };
    let feeAmount = 0;
    switch (type) {
      case 'monthlyPetRent': {
        const txType = automatedTransactionTypes.find((txType) => {
          return txType.type === AUTOMATED_TRANSACTION_TYPES.PETRENT;
        });
        const petRentWasModified = fieldWasModified('monthlyPetRent');
        if (petRentWasModified) {
          feeAmount = lease.monthlyPetRent;
          break;
        }
        const fee = fees.filter((fee) => {
          if (!txType) return false;
          return fee.automatedTransactionTypeId === txType.id;
        });
        if (fee.length > 1) {
          feeAmount = 0;
          break;
        }
        feeAmount = fee.length > 0 ? fee[0].feeAmount : 0;
        break;
      }
      case 'petDeposit': {
        const petDepositWasModified = fieldWasModified('petDeposit');
        if (petDepositWasModified) {
          feeAmount = lease.petDeposit;
          break;
        }
        feeAmount =
          fees.find((fee) => fee.feeTransactionCode === 'PETDEP')?.feeAmount ??
          0;

        break;
      }
      case 'petFee': {
        const txType = automatedTransactionTypes.find((txType) => {
          return txType.type === AUTOMATED_TRANSACTION_TYPES.PETFEE;
        });
        const petFeeWasModified = fieldWasModified('petFee');
        if (petFeeWasModified) {
          feeAmount = lease.petFee;
          break;
        }
        const fee = fees.filter((fee) => {
          if (!txType) return false;
          return fee.automatedTransactionTypeId === txType.id;
        });
        if (fee.length > 1) {
          feeAmount = 0;
          break;
        }

        feeAmount = fee.length > 0 ? fee[0].feeAmount : 0;

        break;
      }
      case 'shortTermFee': {
        const txType = automatedTransactionTypes.find((txType) => {
          return txType.type === AUTOMATED_TRANSACTION_TYPES.SHORTTERM;
        });
        const shortTermFeeWasModified = fieldWasModified('shortTermFee');
        if (shortTermFeeWasModified) {
          feeAmount = lease.shortTermFee;
          break;
        }
        const fee = fees.filter((fee) => {
          if (!txType) return false;
          return fee.automatedTransactionTypeId === txType.id;
        });
        if (fee.length > 1) {
          feeAmount = 0;
          break;
        }
        feeAmount = fee.length > 0 ? fee[0].feeAmount : 0;

        break;
      }
      case 'securityDeposit': {
        const securityDepositWasModified = fieldWasModified('securityDeposit');
        if (securityDepositWasModified) {
          feeAmount = lease.securityDeposit;
          break;
        }
        const floorPlanSecurityDeposit = fees.find(
          (fee) =>
            fee.floorPlanFee.length > 0 && fee.feeTransactionCode === 'SECDEP',
        )?.floorPlanFee[0].feeAmount;

        const unitSecurityDeposit = fees.find(
          (fee) => fee.unitFee !== null && fee.feeTransactionCode === 'SECDEP',
        )?.unitFee.feeAmount;

        const propertySecurityDeposit = fees.find(
          (fee) => fee.feeTransactionCode === 'SECDEP',
        )?.feeAmount;

        const securityDeposit =
          unitSecurityDeposit ||
          floorPlanSecurityDeposit ||
          propertySecurityDeposit ||
          0;

        feeAmount = securityDeposit;

        break;
      }

      case 'nonRefundableAdminFee': {
        const txType = automatedTransactionTypes.find(
          (txType) => txType.type === AUTOMATED_TRANSACTION_TYPES.ADMIN,
        );
        const adminFeeWasModified = fieldWasModified('nonRefundableAdminFee');
        if (adminFeeWasModified) {
          feeAmount = lease.nonRefundableAdminFee;
          break;
        }
        const fee = fees.filter((fee) => {
          if (!txType) return false;
          return fee.automatedTransactionTypeId === txType.id;
        });
        if (fee.length > 1) {
          feeAmount = 0;
          break;
        }
        feeAmount = fee.length > 0 ? fee[0].feeAmount : 0;

        break;
      }
      default: {
        break;
      }
    }
    return feeAmount;
  };
  getLeaseTermInMonths = (leaseTerm: Object) => {
    return leaseTerm.masterLeaseTerm?.nMonths;
  };

  populateAutomatedLeaseCharges = (lease: Object, fees) => {
    if (fees.length === 0) {
      return {
        monthlyPetRent: 0,
        petDeposit: 0,
        shortTermFee: 0,
        petFee: 0,
        securityDeposit: 0,
        nonRefundableAdminFee: 0,
      };
    }
    const {
      monthlyPetRent,
      petDeposit,
      shortTermFee,
      petFee,
      securityDeposit,
      nonRefundableAdminFee,
      autoFilledFields,
    } = this.getFeeAmounts(fees, lease);
    this.captureAutoFilledFields(autoFilledFields);
    return {
      monthlyPetRent,
      petDeposit,
      shortTermFee,
      petFee,
      securityDeposit,
      nonRefundableAdminFee,
    };
  };

  render() {
    const {
      affordableQualifications,
      intl,
      currentApplication,
      currentApplicationId,
      currentApplication: {
        lease = {},
        householdId,
        anyContact = true,
        anyInsurance = true,
      },
      frNames,
      nonfrNames,
      propertyClassType,
      customerId,
      applicationDecisionStatus,
      applicationStatuses,
      users,
      deletedApplicants,
      selectedProperty,
      selectedMonthlyOption,
      leaseSignatureStatuses,
      flags,
      locale,
    } = this.props;
    const { naur: naurFlag } = flags;

    const { firstName, lastName } = this.props.currentApplication.prospectInfo;
    if (!this.props.currentApplication.prospectInfo.id) {
      return <AsyncBox loading={true} sx={{ height: 'calc(100vh - 40px)' }} />;
    }
    const onChecklistOptionChangeCurried = curryN(
      4,
      this.handleChecklistOptionChange.bind(this),
    );

    const members = this.props.applicationHousehold;
    const application = this.props.currentApplication;
    const isUnitAssigned = currentApplication?.au?.unitId;
    const showDocumentsTab = this.checkUserPermissions(['document-read']);
    const showLeaseAppTab = this.checkUserPermissions(['application-update']);
    const canAssignUnitBeforeFee = this.checkUserPermissions([
      'application-assign-unit-before-fee',
    ]);
    const canAssignUnassignUnit = this.checkUserPermissions([
      'manage-units-assign-unassign',
    ]);

    const isCommercial = application.isCommercial === true;

    // $FlowFixMe
    const unit = pathOr(null, ['au', 'unit'], application);
    // $FlowFixMe
    const leaseRentPercentage = pathOr(
      '20',
      ['currentApplication', 'leaseRentPercentage'],
      this.props,
    );
    const isResidentPortalActive = pathOr(
      false,
      ['isResidentPortalActive'],
      selectedProperty,
    );
    // $FlowFixMe
    const vehicleCount = pathOr(null, ['vehicleCount'], application);
    // $FlowFixMe
    const household = pathOr({}, ['householdInfo'], application);

    /**
     * TODO: An honest discussion about why some of these need to be on the
     * Application object
     */
    const leaseDataTabPropsFromCurrentApp = pick(
      [
        'au',
        'applicationStatus',
        'basicLeaseFees',
        'labelEndDate',
        'leaseTerms',
        'prospectInfo',
        'softDeletedLeaseTerms',
      ],
      // $FlowFixMe
      currentApplication,
    );
    const { applicationStatus, au } = leaseDataTabPropsFromCurrentApp;
    const showLeaseDataTab =
      applicationStatus.name === 'Approved' && !isNil(au);
    const leaseUnit = pathOr({}, ['unit'], au);

    const paymentRestrictions = {
      doNotAccept: household.paymentsDoNotAccept,
      certifiedOnly: household.paymentsCertifiedOnly,
    };

    const hasCompletedQualifications = (affordableQualifications || []).some(
      (aq) => !aq.isActive,
    );

    // "Household change would reopen a certification" (Change Head of
    // Household / Mini-Profile Add warnings) is no longer computed here:
    // affordableQualificationHistories is never populated by
    // GET_ONE_APPLICATION_SUCCESS's payload, so it was always empty and the
    // warning could never fire. Household/MiniProfileBridge now source it
    // themselves from the same per-program query the Completed tab renders
    // from -- see useHouseholdWillReopenCertification, fed by
    // affordableProgramIds above.

    const complianceApprovalChanged = this.refetchHudProfileForApplication;

    const customer = pathOr(
      {},
      ['applicantCustomer', 'customer'],
      head(application.applicants),
    );

    const address = pathOr(
      {},
      ['applicantCustomer', 'address'],
      head(application.applicants),
    );

    const showCamTab = false;
    const showTextingTab =
      selectedProperty.isTwoWayCommunicationActive &&
      this.checkUserPermissions(['communication-create']);

    const nonOptionalCharge = getNonOptionalChargeFromQualifications(
      affordableQualifications || [],
    );

    const isAffordable = resolveIsAffordableContext({
      propertyClassType,
      applicantPropertyClass: application?.propertyClass?.name ?? '',
      unit,
    });

    const { programs, affordableTabs, hudTab, householdUtilityAllowanceId } =
      configureResidentApplicantAffordableTabsDisplay(
        affordableQualifications || [],
        selectedProperty,
        unit,
        flags,
        isAffordable,
        true,
      );

    // Feeds useHouseholdWillReopenCertification (Household /
    // MiniProfileBridge) so the Change Head of Household / Mini-Profile Add
    // warnings can query every program this household actually has, not
    // just the one the user happens to be viewing.
    const affordableProgramIds = [
      ...new Set(
        [...affordableTabs, hudTab]
          .map((tab) => tab?.propertyAffordableProgramId)
          .filter(Boolean),
      ),
    ];

    const allAffordableTabsApproved = chkAllTabsApproved(
      [...affordableTabs, hudTab],
      affordableQualifications,
    );
    // The same tabs chkAllTabsApproved just judged, but named, so the lease-action gate tooltip
    // can say which program is blocking instead of "all applicable certifications".
    const unapprovedAffordablePrograms = getUnapprovedProgramNames(
      [...affordableTabs, hudTab],
      affordableQualifications,
    );
    // ENG-32187 / ENG-32886 — the Move-In gate. It disables the generate + execute lease-action
    // buttons (never Save, mirroring the transfer gate) when a program's active MOVE_IN cert is
    // unapproved — including HUD, whose display tab may surface a non-move-in cert. Scopes to the
    // visible qualification tabs, joining by propertyAffordableProgramId so layered/composite
    // phantom certs that map to no approvable tab neither pad the tooltip nor keep the buttons
    // disabled. Distinct from getUnapprovedProgramNames above, which gates on the tab-surfaced cert.
    const moveInCertsApproved = chkAllMoveInCertsApproved(
      [...affordableTabs, hudTab],
      affordableQualifications,
    );
    const unapprovedMoveInPrograms = getUnapprovedMoveInProgramNames(
      [...affordableTabs, hudTab],
      affordableQualifications,
    );

    const disableUnassignUnitLink = chkAllTabsForComplianceApprovalStatuses(
      [...affordableTabs, hudTab],
      affordableQualifications,
      [
        COMPLIANCE_APPROVAL_STATUSES.APPROVED,
        COMPLIANCE_APPROVAL_STATUSES.APPROVED_PENDING_SIGN,
      ],
    );

    const isReceivingAssistance = getIsReceivingAssistance(
      affordableTabs || [],
    );

    const miniProfileApplicant =
      application?.applicants?.find((applicant) => {
        return applicant?.id === this.state.personToEdit?.id;
      }) ?? null;

    // set default annual income value from applicantCustomer (which comes from application forms
    const reportingInformationInitialValues = {
      specialNeedsDesignationIds:
        miniProfileApplicant?.specialNeedsDesignationIds ?? [],
      annualIncome: miniProfileApplicant?.annualIncome
        ? miniProfileApplicant.annualIncome
        : miniProfileApplicant?.applicantCustomer?.annualIncome ?? '0.00',
    };
    const scheduledMoveInDate = this.props.flags.assigningUnitsMoveInDates
      ? getScheduledMoveInDate({
          lease,
          isUnitAssigned,
          prospectPreferences:
            currentApplication?.prospectInfo?.prospectPreferences,
        })
      : lease.moveInDate;

    const appliesForPhaseIn =
      currentApplication?.householdInfo?.householdPhaseInEligibility
        ?.appliesForPhaseIn;

    const section236Flag = this.props.flags?.section236;
    let isSection236 = checkIsSection236({
      shouldCheck: hudTab && section236Flag,
      section236Flag,
      unit,
      selectedProperty,
    });

    return (
      <LeaseDataTabProvider leaseId={lease.id}>
        <DocumentTitle
          title={this.props.intl.formatMessage(messages.title)}
          className="creditstatus-page"
        >
          <NoPrint>
            {naurFlag && (
              <NaurWarning
                variant={isUnitAssigned ? 'unit-impacted' : 'property-generic'}
                propertyId={selectedProperty?.id}
                unitId={isUnitAssigned}
                sx={{ pt: 2, ml: 2, mr: 2 }}
              />
            )}
            <HouseholdNotifications
              organizationId={selectedProperty.organizationId}
              propertyId={selectedProperty.id}
              householdId={householdId}
            />
            <ProfileLayout
              ContactBox={
                <ProfileDetails
                  handleClassChange={this.handleApplicationPropertyClassUpdate}
                  propertyClassType={propertyClassType}
                  propertyClasses={this.props.propertyClasses}
                  intl={this.props.intl}
                  currentRecord={application}
                  handleNotesChange={this.handleApplicationNoteUpdate}
                  isResident={false}
                  prospectUpdateAllowed={this.checkUserPermissions([
                    'prospect-update',
                  ])}
                  assignedUnits={this.props.assignedUnits}
                  applicationStatuses={applicationStatuses}
                  handleStatusChange={this.handleApplicationStatusChange}
                  checkUserPermissions={this.checkUserPermissions}
                  applicationStatusId={application.applicationStatusId}
                />
              }
              Household={
                <>
                  {isCommercial === true && (
                    <Tenant
                      householdId={householdId}
                      householdMembers={members}
                      editHouseholdMember={this.handleHouseholdMemberClick}
                      anyContact={anyContact}
                      anyInsurance={anyInsurance}
                      onContactCheckboxClick={
                        this.handleCommercialContactsCheckboxClick
                      }
                      onInsuranceCheckboxClick={
                        this.handleCommercialInsuranceCheckboxClick
                      }
                    />
                  )}
                  {isCommercial === false && (
                    <Household
                      hasAssignedUnit={isUnitAssigned}
                      householdMembers={members}
                      onEdit={this.handleEditHousehold}
                      applicationId={application.id}
                      vehicleCount={vehicleCount}
                      editHouseholdMember={this.handleHouseholdMemberClick}
                      onAddHouseholdMember={this.addHouseholdMember}
                      handleNoVehiclesChange={this.handleNoVehiclesChange}
                      householdId={householdId}
                      hasCompletedQualifications={hasCompletedQualifications}
                      // application.applicants (not the display-only
                      // `members`/applicationHousehold above) is what the
                      // applicant-path Change Head of Household button needs
                      // -- it carries isPrimary/financiallyResponsible/
                      // applicantTypeId, which applicationHousehold doesn't.
                      applicants={application.applicants}
                      // Lets Household's own reopen-detection query
                      // (see useHouseholdWillReopenCertification) cover
                      // every program this household has, not just the one
                      // tab currently in view.
                      affordableProgramIds={affordableProgramIds}
                      onChangeHeadOfHouseholdSuccess={
                        this.handleMiniProfileSaveSuccess
                      }
                      // Refresh application data after applicant pet writes.
                      onSaveSuccess={this.handleMiniProfileSaveSuccess}
                      setHasHouseholdRentableItems={
                        this.setHasHouseholdRentableItems
                      }
                      refetchHouseholdRentableItemsTrigger={
                        this.state.refetchHouseholdRentableItemsTrigger
                      }
                      // Household-level Affordable/Conventional, same signal
                      // the Mini-Profile itself resolves affordability from
                      // (application.propertyClass.name) -- otherwise
                      // Household falls back to the coarser property-level
                      // heuristic it documents (harmless there today only
                      // because nothing on this screen used isAffordable
                      // before this card).
                      isAffordable={isAffordable}
                    />
                  )}
                  {lease.desiredSignatureMethod ===
                    ELECTRONIC_SIGNING_METHOD && (
                    <LeaseSigning
                      leaseId={lease.id}
                      isCommercial={isCommercial}
                      leaseSignatureStatuses={leaseSignatureStatuses}
                    />
                  )}
                </>
              }
              Waitlist={
                <Waitlist
                  organizationId={this.props.selectedProperty?.organizationId}
                  propertyId={this.props.selectedProperty?.id}
                  applicationId={this.props.currentApplicationId}
                  prospectPreferences={
                    this.props.currentApplication.prospectInfo
                      ?.prospectPreferences
                  }
                  floorPlans={this.props.floorPlans}
                  onRefetch={this.refetchHudProfileForApplication}
                  applicationStatusName={applicationStatus.name}
                />
              }
              Snapshot={
                <Snapshot
                  initialValues={{
                    assignedToId: application.assignedToId,
                  }}
                  applicationId={this.props.currentApplicationId}
                  currentRecord={application}
                  currentUser={this.props.currentUser.user}
                  users={users}
                  activityTypes={this.props.activityTypes}
                  intl={this.props.intl}
                  handleAssignedToChange={this.handleAssignedToChange}
                  handleConvertToResident={this.handleConvertToResident}
                  handleConvertToCommercialTenant={
                    this.handleConvertToCommercialTenant
                  }
                  hasCompletedQualifications={hasCompletedQualifications}
                  hasHouseholdRentableItems={
                    this.state.hasHouseholdRentableItems
                  }
                  history={this.props.history}
                  assignedUnits={this.props.assignedUnits}
                  floorPlans={this.props.floorPlans}
                  scheduledMoveInDate={scheduledMoveInDate}
                  unassignUnitCallback={this.unassignUnitHandler}
                  getUnitQuote={this.props.actions.getUnitQuote}
                  prospectId={this.props.currentApplication.prospectInfo.id}
                  paymentRestrictions={paymentRestrictions}
                  rentStartDate={lease.rentStartDate}
                  refreshActivityTable={this.refreshActivityTable}
                  areApplicationFeesPaid={currentApplication?.areFeesPaid}
                  propertyId={this.props.selectedProperty?.id}
                  organizationId={this.props.selectedProperty?.organizationId}
                  householdId={householdId}
                  disableUnassignUnitLink={disableUnassignUnitLink}
                  isAffordable={isAffordable}
                  canAssignUnitBeforeFee={canAssignUnitBeforeFee}
                  canAssignUnassignUnit={canAssignUnassignUnit}
                  onSelectFloorPlan={this.openSelectFloorPlanModal}
                  onDesiredMoveInDateSaved={
                    this.refetchApplicationAndQualifications
                  }
                />
              }
              Tabs={
                <Tabs
                  id="application-profile-tabs"
                  value={this.state.key}
                  onChange={(key) => this.setState({ ...this.state, key })}
                  checkUserPermissions={this.checkUserPermissions}
                  affordableTabs={affordableTabs}
                  flags={flags}
                  householdId={householdId}
                  hudTab={hudTab}
                  isCommercial={isCommercial}
                  programs={programs}
                  selectedProperty={selectedProperty}
                  showCamTab={showCamTab}
                  showDocumentsTab={showDocumentsTab}
                  showLeaseAppTab={showLeaseAppTab}
                  showTextingTab={showTextingTab}
                />
              }
              TabsContent={
                <>
                  {this.state.key === TAB_KEYS.PROSPECT_INFO && (
                    <>
                      {!this.state.editModeProspectInfo &&
                        !!this.props.currentApplication.prospectInfo && (
                          <ProspectInfoTab
                            intl={this.props.intl}
                            handleProspectInfoEdit={this.handleProspectInfoEdit}
                            prospect={
                              this.props.currentApplication.prospectInfo
                            }
                            petTypes={this.props.petTypes}
                            contactTypes={this.props.contactTypes}
                            referralTypes={this.props.referralTypes}
                            floorPlans={this.props.floorPlans}
                            isWaitlistV2Enabled={
                              this.props.flags.affordableWaitlistV2
                            }
                          />
                        )}
                      {this.state.editModeProspectInfo && (
                        <ProspectInfoTabEditMode
                          intl={this.props.intl}
                          handleProspectInfoEdit={this.handleProspectInfoEdit}
                          petTypes={this.getDropDownOptionsForPets(
                            this.props.petTypes,
                          )}
                          contactTypes={this.getDropDownOptions(
                            this.props.contactTypes,
                          )}
                          referralTypes={this.getDropDownOptions(
                            this.props.referralTypes,
                          )}
                          floorPlans={this.props.floorPlans}
                          initialValues={this.getProspectForForm()}
                          onSubmit={this.handleSubmitProspectInfoTab}
                          handleCancel={this.handleCancelProspectInfoTab}
                          applicationId={this.props.currentApplicationId}
                        />
                      )}
                    </>
                  )}
                  {this.state.key === TAB_KEYS.ACTIVITY && (
                    <ActivityContainer
                      intl={this.props.intl}
                      onCreateActivityClick={this.onCreateActivityClick}
                      onEditActivity={this.onEditActivity}
                      prospectId={this.props.currentApplication.prospectInfo.id}
                      updateTrigger={this.state.activityTableUpdateTrigger}
                      locale={this.props.locale}
                    />
                  )}
                  {this.state.key === TAB_KEYS.TEXTING && (
                    <TextingTab householdId={householdId} />
                  )}
                  {this.state.key === TAB_KEYS.DOCUMENTS && (
                    <ManageDocuments
                      intl={this.props.intl}
                      applicationId={this.props.currentApplicationId}
                      application={this.props.currentApplication}
                      customerType="applicant"
                      deletedApplicants={deletedApplicants}
                    />
                  )}
                  {this.state.key === TAB_KEYS.LEDGER && (
                    <Ledger
                      customerStatus="Applicant"
                      customerId={customerId}
                      customerName={`${firstName} ${lastName}`}
                      frMembers={this.props.financiallyResponsibleHousehold}
                      frNames={this.props.frNames}
                      unit={unit}
                      resendClick={this.onResendPayleaseClick}
                      household={household}
                      applicationId={this.props.currentApplicationId}
                      isResident={false}
                      onZeroBalance={this.onZeroBalance}
                      householdMembers={members}
                      lateMethodId={lease?.lateMethodId}
                    />
                  )}
                  {this.state.key === TAB_KEYS.LEASE_APPLICATION && (
                    <LeaseApplicationTab
                      intl={this.props.intl}
                      currentRecord={application}
                      initialValues={{
                        applicationStatusId: application.applicationStatusId,
                      }}
                      affordableRelationships={
                        this.state.affordableRelationships
                      }
                      applicationId={this.props.currentApplicationId}
                      floorPlans={this.props.floorPlans}
                      applicationDecisionStatus={applicationDecisionStatus}
                      applicationStatuses={applicationStatuses}
                      handleChecklistOptionChange={
                        onChecklistOptionChangeCurried
                      }
                      checkUserPermissions={this.checkUserPermissions}
                      handleStatusChange={this.handleApplicationStatusChange}
                      handleNotesChange={this.handleApplicationNoteUpdate}
                      screening={this.props.screening}
                      screeningLetterTypes={this.props.screeningLetterTypes}
                      submitScreeningApplication={
                        this.props.actions.submitScreeningApplication
                      }
                      refreshActivityTable={this.refreshActivityTable}
                      saveScreeningResults={
                        this.props.actions.saveScreeningResults
                      }
                      generateAdverseActionLetter={
                        this.props.actions.generateAdverseActionLetter
                      }
                      handleEditApplicationForm={this.handleEditApplicationForm}
                      currentUser={this.props.currentUser}
                      promptToaster={this.props.actions.promptToaster}
                      selectedProperty={selectedProperty}
                      householdId={householdId}
                      handleMultipleChecklistOptionsChange={
                        this.handleMultipleChecklistOptionsChange
                      }
                      applicationUpdating={this.state.applicationUpdating}
                      onResetSuccess={() => {
                        this.props.actions.getOneApplication(
                          this.props.currentApplicationId,
                        );
                      }}
                    />
                  )}

                  {this.state.key === TAB_KEYS.LEASE_DATA && (
                    <>
                      {isCommercial === true && (
                        <>
                          {!showLeaseDataTab ? (
                            <LeaseDataTabEmpty isCommercial={true} />
                          ) : (
                            <CommercialLeaseDataTab
                              frNames={frNames}
                              leases={[{ ...lease, units: [leaseUnit] }]}
                              isResident={false}
                              labelEndDate={
                                leaseDataTabPropsFromCurrentApp.labelEndDate
                              }
                              customer={customer}
                              unit={leaseUnit}
                              lateMethods={this.props.lateMethods || []}
                              selectedMonthlyOption={selectedMonthlyOption}
                              onLeaseBasicsSubmit={
                                this.handleCommercialLeaseBasicsSubmit
                              }
                              applicationId={this.props.currentApplicationId}
                              prospectInfo={
                                leaseDataTabPropsFromCurrentApp.prospectInfo
                              }
                            />
                          )}
                        </>
                      )}

                      {isCommercial === false && (
                        <>
                          {!showLeaseDataTab ? (
                            <LeaseDataTabEmpty />
                          ) : (
                            <LeaseDataTab
                              {...leaseDataTabPropsFromCurrentApp}
                              allAffordableTabsApproved={
                                allAffordableTabsApproved
                              }
                              unapprovedAffordablePrograms={
                                unapprovedAffordablePrograms
                              }
                              moveInCertsApproved={moveInCertsApproved}
                              unapprovedMoveInPrograms={
                                unapprovedMoveInPrograms
                              }
                              applicationId={this.props.currentApplicationId}
                              frNames={frNames}
                              lateMethods={this.props.lateMethods}
                              leaseRentPercentage={leaseRentPercentage}
                              leases={[
                                {
                                  ...this.state.leaseWithAutoFilledCharges,
                                  units: [leaseUnit],
                                },
                              ]}
                              leaseSignatureStatuses={leaseSignatureStatuses}
                              monthlyTransactions={
                                this.props.monthlyTransactions
                              }
                              hasNonFR={nonfrNames.length > 0}
                              onGenerate={this.leaseDataHandleGenerateLease}
                              onSubmit={this.leaseDataHandleSubmit}
                              openFiscalPeriod={this.props.openFiscalPeriod}
                              securityDeposits={this.props.securityDeposits}
                              selectedMonthlyOption={selectedMonthlyOption}
                              utilityAllowanceId={householdUtilityAllowanceId}
                              isReceivingAssistance={isReceivingAssistance}
                              nonOptionalCharge={nonOptionalCharge}
                              setProratedRentAmount={setProratedRentAmount}
                              setProratedOtherAmount={setProratedOtherAmount}
                              resetProratedRentAutoFilledChangeStatus={
                                this.resetProratedRentAutoFilledChangeStatus
                              }
                              handleAutoFilledFieldChange={
                                this.handleAutoFilledFieldChange
                              }
                              autoFilledLeaseChargeFields={
                                this.state.autoFilledLeaseChargeFields
                              }
                            />
                          )}
                        </>
                      )}
                    </>
                  )}

                  {this.state.key === TAB_KEYS.HOUSING_ASSISTANCE_VOUCHER && (
                    <HousingAssistanceVoucherTab
                      organizationId={selectedProperty.organizationId}
                      propertyId={selectedProperty.id}
                      householdId={householdId}
                      householdMoveInDate={moment(scheduledMoveInDate)}
                      stage="applicant"
                      intl={this.props.intl}
                    />
                  )}

                  {this.state.key === TAB_KEYS.CAM && (
                    <CamTab
                      unit={unit}
                      selectedProperty={selectedProperty}
                      householdId={householdId}
                      intl={intl}
                      promptToaster={this.props.actions.promptToaster}
                    />
                  )}

                  {affordableTabs &&
                    affordableTabs.map(
                      ({
                        affordableQualificationId,
                        programId,
                        programName,
                        urlProgramName,
                        propertyAffordableProgramId,
                      }) => {
                        const commonProps = {
                          affordableQualificationId,
                          applicationId: currentApplicationId,
                          history: this.props.history,
                          householdId,
                          initialCertTab: this.props.certTab,
                          intl,
                          key: urlProgramName,
                          locale,
                          location: this.props.location,
                          onComplianceApprovalChange: complianceApprovalChanged,
                          programs,
                          programId,
                          programName,
                          propertyAffordableProgramId,
                          tabKey: this.state.key,
                          unit,
                        };

                        return this.state.key === urlProgramName ? (
                          <>
                            {isGeneralAffordableNonHUD(programName) ? (
                              <GeneralAffordableNonHUD
                                {...commonProps}
                                refetchProfile={() =>
                                  this.props.actions.getOneApplication(
                                    this.props.currentApplicationId,
                                  )
                                }
                              />
                            ) : null}
                            {programName === PH_PROGRAM_NAME ? (
                              <PublicHousing
                                {...commonProps}
                                appliesForPhaseIn={appliesForPhaseIn}
                              />
                            ) : null}
                            {programName === RD_PROGRAM_NAME ? (
                              <GeneralAffordableRD
                                {...commonProps}
                                appliesForPhaseIn={appliesForPhaseIn}
                              />
                            ) : null}
                          </>
                        ) : null;
                      },
                    )}
                  {this.state.key === TAB_KEYS.HUD && (
                    <GeneralAffordableHUD
                      tabKey={this.state.key}
                      history={this.props.history}
                      initialCertTab={this.props.certTab}
                      location={this.props.location}
                      affordableQualificationId={
                        hudTab.affordableQualificationId
                      }
                      applicationId={currentApplicationId}
                      householdId={householdId}
                      programId={hudTab.programId}
                      programName={hudTab.programName}
                      propertyAffordableProgramId={
                        hudTab.propertyAffordableProgramId
                      }
                      appliesForPhaseIn={appliesForPhaseIn}
                      isSection236={isSection236}
                      intl={intl}
                      locale={locale}
                      unit={unit}
                      refetchProfile={this.refetchHudProfileForApplication}
                      residentAffordableQualifications={
                        affordableQualifications ??
                        EMPTY_RESIDENT_AFFORDABLE_QUALIFICATIONS
                      }
                      onComplianceApprovalChange={complianceApprovalChanged}
                    />
                  )}
                </>
              }
            />

            <PersonalInformationModal
              onSubmit={this.handleEditApplication}
              personalInfo={this.state.personToEdit}
              onClose={this.resetModal}
              show={this.state.modal === PERSONAL_INFORMATION_FORM}
              residentPortalInfo={{
                display: isResidentPortalActive,
                sendPortalInviteEmail: this.sendPortalInviteEmail,
                isAffordaPortal: this.props.flags?.affordaPortaV1 ?? false,
              }}
              isCommercial={isCommercial}
              isAffordable={isAffordable}
              unit={unit}
              states={this.props.states}
              lease={lease}
              initialValues={{
                generalInformation: { ...customer, ...address },
                reportingInformation: reportingInformationInitialValues,
              }}
              flags={this.props.flags}
              applicant={miniProfileApplicant}
              property={selectedProperty}
              application={this.props.currentApplication}
              selectedProperty={this.props.selectedProperty}
              onResetSuccess={() => {
                this.props.actions.getOneApplication(
                  this.props.currentApplicationId,
                );
              }}
            />
            <AddHouseholdMemberModal
              open={this.state.modal === 'addHouseholdMemberType'}
              onClose={this.resetModal}
              onContinue={this.handleAddHouseholdMemberTypeContinue}
            />
            <MiniProfileBridge
              open={this.state.modal === 'editAffordableMiniProfile'}
              title={
                this.state.occupantToEdit
                  ? // applicationHousehold (Redux, display-only) already
                    // carries each member's name -- MiniProfileBridge/
                    // useMiniProfileSubject resolve the real, save-ready
                    // record from `applicants` below independently.
                    this.props.applicationHousehold.find(
                      (member) => member.id === this.state.occupantToEdit,
                    )?.name ?? ''
                  : // Read off newMemberType (not occupantToEdit) so the
                  // title and the form the Mini-Profile actually builds
                  // can't disagree -- same as ResidentProfile's
                  // newOccupantInfo.type.
                  this.state.newMemberType === 'minor'
                  ? 'Add Minor'
                  : 'Add Adult'
              }
              onClose={this.resetModal}
              occupantInfo={
                this.state.occupantToEdit
                  ? { id: this.state.occupantToEdit }
                  : { type: this.state.newMemberType || 'adult' }
              }
              subject={{
                kind: 'applicant',
                applicationId: application.id,
                householdId,
              }}
              applicants={application.applicants}
              isFetchingApplicants={this.props.isFetchingCurrentApplication}
              affordableProgramIds={affordableProgramIds}
              suffixes={this.props.suffixes}
              relationshipOptions={this.props.relationshipOptions}
              minorRelationshipOptions={this.props.minorRelationshipOptions}
              applicantTypeOptions={this.props.applicantTypeOptions}
              agedUpMinorApplicantTypeId={this.props.agedUpMinorApplicantTypeId}
              guarantorOptions={this.props.guarantorOptions}
              liveInCaretakerOptions={this.props.liveInCaretakerOptions}
              states={this.props.states}
              countries={this.props.countries}
              affordableRelationships={this.props.affordableRelationships}
              specialNeedsDesignations={this.props.specialNeedsDesignations}
              isAffordable={isAffordable}
              isCommercial={isCommercial}
              onSaveSuccess={this.handleMiniProfileSaveSuccess}
            />

            <SelectFloorPlanModal
              open={this.state.modal === SELECT_FLOOR_PLAN_MODAL}
              onClose={this.resetModal}
              organizationId={this.props.selectedProperty?.organizationId}
              propertyId={this.props.selectedProperty?.id}
              applicationId={this.props.currentApplicationId}
              floorPlans={this.props.floorPlans}
              nBedsArr={nBedsArrInitialValueBuilder(
                application?.prospectInfo?.prospectPreferences?.nBedsArr,
                application?.prospectInfo?.prospectPreferences?.nBeds,
              )}
              storedFloorPlanId={
                application?.prospectInfo?.prospectPreferences
                  ?.preferredFloorPlanId
              }
              onSaved={() => {
                // The Snapshot row and Prospect Information read redux, not react-query, so the
                // modal's own react-query invalidation is not enough to refresh them.
                this.props.actions.getOneApplication(
                  this.props.currentApplicationId,
                );
              }}
            />
          </NoPrint>
        </DocumentTitle>
      </LeaseDataTabProvider>
    );
  }
}

ApplicationProfile.contextTypes = {
  store: PropTypes.any,
};

export const mapStateToProps = (
  state: GlobalState,
  ownProps: Object,
): Props => {
  const { app, applicationProfile, leaseDataTab, languageProvider } = state;
  const { flags } = ownProps;
  return {
    activeTab: pathOr(
      TAB_KEYS.ACTIVITY,
      ['tab'],
      parse(ownProps.location.search.replace('?', '')),
    ),
    certTab: pathOr(
      null,
      ['certTab'],
      parse(ownProps.location.search.replace('?', '')),
    ),
    activityTypes: app.activityTypes,
    affordableQualifications: applicationProfile.affordableQualifications,
    // Mini-Profile Add/Edit (this card) -- same App-wide option lists
    // ResidentProfile already threads to its own MiniProfileBridge/
    // AddHouseholdMemberModal.
    affordableRelationships: getAffordableRelationshipOptions(state),
    agedUpMinorApplicantTypeId: getAgedUpMinorApplicantTypeId(state),
    applicantTypeOptions: getApplicantTypeOptions(state),
    // Feed the Mini-Profile's Type cascade (useMiniProfileSections) -- the
    // legacy application page never had it, so these are new here.
    guarantorOptions: getGuarantorOptions(state),
    liveInCaretakerOptions: getLiveInCaretakerOptions(state),
    countries: getCountryOptions(state),
    minorRelationshipOptions: getMinorRelationshipOptions(state),
    relationshipOptions: getRelationshipOptions(state),
    suffixes: getNameSuffixOptions(state),
    applicationDecisionStatus: applicationProfile.applicationDecisionStatuses,
    applicationHousehold: getApplicationHousehold(state),
    applicationStatuses: getApplicationStatusOptions(state),
    assignedUnits: applicationProfile.assignedUnits,
    columnOrder: applicationProfile.columnOrder,
    completedActivities: applicationProfile.completedActivities,
    completedActivitiesMeta: applicationProfile.completedActivitiesMeta,
    allActivities: applicationProfile.allActivities,
    allActivitiesMeta: applicationProfile.allActivitiesMeta,
    contactTypes: app.contactTypes,
    currentApplication: getApplication(state, flags),
    currentApplicationId: ownProps.match.params.applicationId,
    isFetchingCurrentApplication:
      applicationProfile.isFetchingCurrentApplication,
    currentUser: getCurrentUser(state),
    customerId: getCustomerId(state),
    deletedApplicants: state.applicationProfile.deletedApplicants,
    financiallyResponsibleHousehold: getFinanciallyResponsibleApplicants(state),
    frNames: getFinanciallyResponsibleFullNames(state),
    nonfrNames: getNonfinanciallyResponsibleAdults(state),
    lateMethods: getLateMethods(state),
    screeningLetterTypes: getScreeningLetterTypes(state),
    leaseSignatureStatuses: leaseDataTab.leaseSignatureStatuses,
    locale: languageProvider.locale,
    monthlyTransactions: applicationProfile.monthlyTransactions,
    openFiscalPeriod: applicationProfile.openFiscalPeriod,
    // $FlowFixMe
    organizationId: pathOr('', ['selectedProperty', 'organizationId'], app),
    propertyClasses: app.propertyClasses,
    pendingActivities: applicationProfile.pendingActivities,
    petBreeds: app.petBreeds,
    petTypes: app.petTypes,
    propertyClassType: getSelectedPropertyClassType(state),
    // $FlowFixMe
    propertyId: pathOr('', ['selectedProperty', 'id'], app),
    relationships: app.relationships,
    referralTypes: app.referralTypes,
    screening: applicationProfile.screening,
    securityDeposits: getSecurityDeposits(state),
    selectedMonthlyOption: applicationProfile.selectedMonthlyOption,
    selectedProperty: app.selectedProperty,
    users: getProspectAssigneesOptions(state),
    states: getStateOptions(state),
    incomeTypes: getIncomeTypeOptions(state),
    specialNeedsDesignations: getSpecialNeedsDesignationOptions(state),
    fpNonOptionalCharges: applicationProfile?.fpNonOptionalCharges ?? [],
    floorPlans: app.floorPlans,
  };
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators(
    {
      ...applicationActions,
      ...prospectActions,
      ...manageDocumentActions,
      change,
      editLease,
      getAllActivityTypes,
      getAllAffordableRelationships,
      getAllApplicantTypes,
      getAllAssignees,
      getAllCountries,
      getAllPetTypes,
      getAllPropertyClasses,
      getAllPetBreeds,
      getAllReferralTypes,
      getAllContactTypes,
      getAllNameSuffixes,
      getAllProspectStatus,
      getAllRelationships,
      getSignatureStatuses,
      selectProperty,
      updateSelectedProperty,
      getAllStates,
      getAllIncomeTypes,
      getAllSpecialNeedsDesignations,
      getAllFloorPlans,
      promptToaster,
    },
    dispatch,
  );
  return { actions };
}

const InjectedApplicationProfile = injectIntl(ApplicationProfile);
export default withLDConsumer()(
  connect(mapStateToProps, mapDispatchToProps)(InjectedApplicationProfile),
);
